import { create } from 'zustand';

import type { SSEEvent } from '@repo/shared-types';

// Tool call display data for UI rendering
export interface ToolCallDisplay {
  tool_name: string;
  success: boolean;
  formatted_text: string;
}

export interface UIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  thinkingContent?: string;
  toolCalls?: ToolCallDisplay[];
}

interface ActiveTool {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
}

interface CompletedTool {
  name: string;
  success: boolean;
  formatted_text: string;
}

interface ChatState {
  currentChatId: string | null;
  messages: UIMessage[];
  isStreaming: boolean;
  streamingMessage: string;
  thinkingContent: string;
  activeTools: ActiveTool[];
  completedTools: CompletedTool[];
  abortController: AbortController | null;
  isOpen: boolean;
  pendingReportPayload: unknown | null;
  focusMetric: ((metricKey: string) => void) | null;
  metricLabels: Record<string, string> | null;

  // Actions
  startNewChat: () => void;
  setCurrentChatId: (chatId: string | null) => void;
  setMessages: (messages: UIMessage[]) => void;
  addUserMessage: (content: string) => void;
  handleSSEEvent: (event: SSEEvent) => void;
  finishStreaming: (chatId: string) => void;
  clearStreamingState: () => void;
  setAbortController: (controller: AbortController | null) => void;
  cancelStreaming: () => void;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  setPendingReport: (payload: unknown | null) => void;
  setDashboardContext: (
    focusMetric: ((metricKey: string) => void) | null,
    metricLabels: Record<string, string> | null,
  ) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  currentChatId: null,
  messages: [],
  isStreaming: false,
  streamingMessage: '',
  thinkingContent: '',
  activeTools: [],
  completedTools: [],
  abortController: null,
  isOpen: false,
  pendingReportPayload: null,
  focusMetric: null,
  metricLabels: null,

  startNewChat: () => {
    set({
      currentChatId: null,
      messages: [],
      isStreaming: false,
      streamingMessage: '',
      thinkingContent: '',
      activeTools: [],
      completedTools: [],
      pendingReportPayload: null,
    });
  },

  setCurrentChatId: (chatId) => {
    set({ currentChatId: chatId });
  },

  setMessages: (messages) => {
    set({ messages });
  },

  addUserMessage: (content) => {
    const userMessage: UIMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    set((state) => ({
      messages: [...state.messages, userMessage],
      isStreaming: true,
      streamingMessage: '',
      thinkingContent: '',
      activeTools: [],
      completedTools: [],
    }));
  },

  handleSSEEvent: (event) => {
    switch (event.type) {
      case 'thinking':
        // Real model reasoning streams as deltas — accumulate it.
        set((state) => ({
          thinkingContent: state.thinkingContent + event.content,
        }));
        break;

      case 'tool_start':
        set((state) => ({
          activeTools: [
            ...state.activeTools,
            { id: crypto.randomUUID(), name: event.tool_name, arguments: event.arguments },
          ],
        }));
        break;

      case 'tool_result':
        set((state) => {
          const index = state.activeTools.findIndex((t) => t.name === event.tool_name);
          return {
            activeTools:
              index >= 0
                ? [...state.activeTools.slice(0, index), ...state.activeTools.slice(index + 1)]
                : state.activeTools,
            completedTools: [
              ...state.completedTools,
              {
                name: event.tool_name,
                success: event.tool_result.success,
                formatted_text: event.formatted_text,
              },
            ],
          };
        });
        // A `pdf` tool returns { pdf: { title, columns, rows, ... } } for the
        // client to render + offer as a download.
        {
          const data = event.tool_result.data as { pdf?: unknown } | undefined;
          if (event.tool_result.success && data?.pdf) {
            set({ pendingReportPayload: data.pdf });
          }
        }
        break;

      case 'response':
        set((state) => ({
          streamingMessage: state.streamingMessage + event.content,
        }));
        break;

      case 'error': {
        // Add error as assistant message
        const errorMessage: UIMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `Error: ${event.message}`,
          timestamp: new Date(),
        };
        set((state) => ({
          messages: [...state.messages, errorMessage],
          isStreaming: false,
          streamingMessage: '',
          thinkingContent: '',
          activeTools: [],
          completedTools: [],
        }));
        break;
      }

      case 'done':
        // This is handled by finishStreaming
        break;
    }
  },

  finishStreaming: (chatId) => {
    set((state) => {
      if (state.streamingMessage) {
        // Convert completed tools to tool call display format
        const toolCalls: ToolCallDisplay[] = state.completedTools.map((t) => ({
          tool_name: t.name,
          success: t.success,
          formatted_text: t.formatted_text,
        }));

        const assistantMessage: UIMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: state.streamingMessage,
          timestamp: new Date(),
          thinkingContent: state.thinkingContent || undefined,
          toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        };

        return {
          currentChatId: chatId,
          messages: [...state.messages, assistantMessage],
          isStreaming: false,
          streamingMessage: '',
          thinkingContent: '',
          activeTools: [],
          completedTools: [],
        };
      } else {
        return {
          currentChatId: chatId,
          isStreaming: false,
          streamingMessage: '',
          thinkingContent: '',
          activeTools: [],
          completedTools: [],
        };
      }
    });
  },

  clearStreamingState: () => {
    set({
      isStreaming: false,
      streamingMessage: '',
      thinkingContent: '',
      activeTools: [],
      completedTools: [],
    });
  },

  setAbortController: (controller) => {
    set({ abortController: controller });
  },

  cancelStreaming: () => {
    const { abortController, messages } = get();
    // Abort the in-flight request
    if (abortController) {
      abortController.abort();
    }
    // Remove the last user message (clean slate - as if they never sent it)
    const lastMessage = messages[messages.length - 1];
    const newMessages = lastMessage?.role === 'user' ? messages.slice(0, -1) : messages;
    // Reset all streaming state
    set({
      abortController: null,
      isStreaming: false,
      streamingMessage: '',
      thinkingContent: '',
      activeTools: [],
      completedTools: [],
      messages: newMessages,
      pendingReportPayload: null,
    });
  },

  togglePanel: () => {
    set((state) => ({ isOpen: !state.isOpen }));
  },

  openPanel: () => {
    set({ isOpen: true });
  },

  closePanel: () => {
    set({ isOpen: false });
  },

  setPendingReport: (payload) => {
    set({ pendingReportPayload: payload });
  },

  setDashboardContext: (focusMetric, metricLabels) => {
    set({ focusMetric, metricLabels });
  },
}));
